import type { MantineStyleProps } from '@mantine/core';
import { Box, Button, Group, Stack, Title } from '@mantine/core';
import { RichTextEditor } from '@mantine/tiptap';
import { useEffect, useRef, useState } from 'react';

import { useTranslation } from '@apple/lib/i18next';
import {
	getTextEditorHtml,
	isTextEditorReady,
	useTextEditor,
} from '@apple/ui/form/hooks/useTextEditor';
import { FormattedDate } from '@apple/utils/globalization/components/FormattedDate';

import { acknowledgeContent } from '../api/announcement';
import type { Announcement } from '../models/announcement';
import classes from './AnnouncementItem.module.css';

type AnnouncementItemOptions = {
	showTitle?: boolean;
	showCreatedDate?: boolean;
};

type AnnouncementItemProps = MantineStyleProps & {
	announcement: Announcement;
	markReadOnView?: boolean;
	titleSection?: React.ReactNode;
	bottomSection?: React.ReactNode;
	onViewMore?: () => void;
	options?: AnnouncementItemOptions;
};

export function AnnouncementItem({
	announcement,
	markReadOnView,
	titleSection,
	bottomSection,
	onViewMore,
	options,
	...styles
}: AnnouncementItemProps) {
	const { showTitle = true, showCreatedDate = false } = options ?? {};
	const { t } = useTranslation('common');
	const contentRef = useRef<HTMLDivElement>(null);
	const [isOverflowing, setIsOverflowing] = useState(false);
	const canViewMore = Boolean(onViewMore);

	const editor = useTextEditor({
		content: announcement.content,
		editable: false,
	});
	const hasEditor = isTextEditorReady(editor);

	useEffect(() => {
		const content = getTextEditorHtml(editor);
		if (content !== null && content !== announcement.content) {
			editor.commands.setContent(announcement.content);
		}
		if (!announcement.acknowledged && markReadOnView) {
			void acknowledgeContent(announcement.contentId);
		}
	}, [
		announcement.acknowledged,
		announcement.content,
		announcement.contentId,
		editor,
		markReadOnView,
	]);

	useEffect(() => {
		if (!canViewMore || !hasEditor) {
			setIsOverflowing(false);
			return;
		}

		const el = contentRef.current;
		if (!el) {
			return;
		}
		const contentEl = el;

		function updateOverflow() {
			const nextIsOverflowing = contentEl.scrollHeight > contentEl.clientHeight + 1;
			setIsOverflowing(current =>
				current === nextIsOverflowing ? current : nextIsOverflowing,
			);
		}

		updateOverflow();

		if (typeof ResizeObserver === 'undefined') {
			window.addEventListener('resize', updateOverflow);
			return () => window.removeEventListener('resize', updateOverflow);
		}

		const resizeObserver = new ResizeObserver(updateOverflow);
		resizeObserver.observe(contentEl);

		if (contentEl.firstElementChild) {
			resizeObserver.observe(contentEl.firstElementChild);
		}

		return () => resizeObserver.disconnect();
	}, [canViewMore, hasEditor]);

	return (
		<Stack justify='space-between' h='100%' {...styles} gap={0} mih={0}>
			<Stack flex={canViewMore ? 1 : undefined} mih={canViewMore ? 0 : undefined}>
				{(showTitle || showCreatedDate || titleSection) && (
					<Group justify='space-between' mt='xl'>
						{showTitle && <Title order={5}>{announcement.title}</Title>}
						{showCreatedDate && (
							<FormattedDate
								data-testid='created-date'
								value={announcement.createdDate}
								size='xs'
							/>
						)}
						{titleSection}
					</Group>
				)}

				{isTextEditorReady(editor) && (
					<Box
						ref={contentRef}
						style={{
							flex: canViewMore ? '1 1 0' : undefined,
							minHeight: canViewMore ? 0 : undefined,
							overflow: canViewMore ? 'hidden' : undefined,
							position: 'relative',
						}}
					>
						<RichTextEditor editor={editor} className={classes.textEditor}>
							<RichTextEditor.Content />
						</RichTextEditor>
						{isOverflowing && <div className={classes.fade} />}
					</Box>
				)}

				{isOverflowing && onViewMore && (
					<Button
						variant='transparent'
						size='compact-xs'
						onClick={onViewMore}
						style={{ alignSelf: 'start' }}
					>
						{t('buttons.viewMore')}
					</Button>
				)}
			</Stack>
			{bottomSection}
		</Stack>
	);
}
